  import React, { useEffect, useMemo, useState } from "react";

 import {
   BadgeCheck,
   Building2,
   ChevronLeft,
   ChevronRight,
   Clock3,
   Eye,
   MapPin,
   Phone,
   ReceiptText,
   Star,
   Store,
   Tags,
   Truck,
   UtensilsCrossed,
 } from "lucide-react";

  // =====================================================
  // STATIC GALLERY FALLBACK IMAGES
  // =====================================================

  import image1 from "../Images/AAHARAM BG Banner (800x550).webp";
  import image2 from "../Images/CHAKNA FOOD BG Banner (800x550)_1x.webp";
  import image3 from "../Images/Chawlas Veg BG Banner (800x550).webp";
  import image4 from "../Images/DELHI (DKMR) BG Banner (800x550)_1x.webp";
  import image5 from "../Images/GURU KRIPA Banner ITEM 1 (500x500)_1x.webp";

  const FALLBACK_GALLERY_IMAGES = [
    image1,
    image2,
    image3,
    image4,
    image5,
  ];

  export default function RestaurantAboutGallery({ restaurantDetails = null }) {
    const [activeIndex, setActiveIndex] = useState(0);

    const [isGalleryHovered, setIsGalleryHovered] = useState(false);

    // =====================================================
    // API DATA
    // =====================================================

  const restaurantName = String(
    restaurantDetails?.restaurantName ||
      restaurantDetails?.restaurant_name ||
      restaurantDetails?.name ||
      "Restaurant",
  ).trim();

    const restaurantImage = String(
      restaurantDetails?.restaurantImage || "",
    ).trim();

    

    const phoneNumber = String(
      restaurantDetails?.phoneNumber ||
        restaurantDetails?.restaurant_phone ||
        restaurantDetails?.phone ||
        "",
    ).trim();

  const address = String(restaurantDetails?.address || "").trim();

  // =====================================================
  // RESTAURANT DESCRIPTION
  // =====================================================

  const apiRestaurantDescription = String(
    restaurantDetails?.restaurantDescription ||
      restaurantDetails?.restaurant_description ||
      restaurantDetails?.description ||
      "",
  ).trim();

  const defaultRestaurantDescription = `${restaurantName} offers freshly prepared food with quality ingredients and hygienic cooking. Enjoy takeaway, home delivery, or dining based on availability, with a variety of dishes to suit different tastes.`;

  const restaurantDescription =
    apiRestaurantDescription || defaultRestaurantDescription;

    const openingTime = String(restaurantDetails?.openingTime || "").trim();

    const closingTime = String(restaurantDetails?.closingTime || "").trim();

    const isOpen = restaurantDetails?.isOpen === true;

    const provideDelivery = restaurantDetails?.provideDelivery === true;

    const provideDining = restaurantDetails?.provideDining === true;

    const businessInformation = restaurantDetails?.businessInformation || {};

    const gstNumber = String(businessInformation?.gstNumber || "").trim();

    const fssaiNumber = String(businessInformation?.fssaiNumber || "").trim();

    const legalName = String(businessInformation?.legalName || "").trim();

    const rating = Number(restaurantDetails?.rating || 0);

    const reviewCount = Number(restaurantDetails?.reviewCount || 0);

    const categories = Array.isArray(restaurantDetails?.categories)
      ? restaurantDetails.categories
      : [];

    const isRestaurantHidden = restaurantDetails?.isRestaurantHidden === true;

    // =====================================================
    // GALLERY
    //
    // IMPORTANT:
    // restaurantImage gallery me include NAHI hogi.
    //
    // galleryImages API se milti hain:
    //   -> sirf wahi gallery me show hongi
    //
    // galleryImages empty / null:
    //   -> static fallback images show hongi
    // =====================================================

    const apiGalleryImages = useMemo(() => {
      const gallery = Array.isArray(restaurantDetails?.galleryImages)
        ? restaurantDetails.galleryImages
        : [];

      return [
        ...new Set(
          gallery.map((image) => String(image || "").trim()).filter(Boolean),
        ),
      ];
    }, [restaurantDetails?.galleryImages]);

    const isUsingFallbackGallery = apiGalleryImages.length === 0;

    const validImages = useMemo(() => {
      if (apiGalleryImages.length > 0) {
        return apiGalleryImages;
      }

      return FALLBACK_GALLERY_IMAGES;
    }, [apiGalleryImages]);

    const hasImages = validImages.length > 0;

    const activeImage = validImages[activeIndex] || FALLBACK_GALLERY_IMAGES[0];

    // =====================================================
    // RESTAURANT PROFILE IMAGE
    //
    // API restaurantImage name ke left me show hogi.
    // Agar API image missing/broken ho to ek static image.
    // =====================================================

    const restaurantProfileImage = restaurantImage || image1;

    // =====================================================
    // TIMING
    // =====================================================

    const operatingHours = useMemo(() => {
      if (openingTime && closingTime) {
        return `${openingTime} - ${closingTime}`;
      }

      if (openingTime) {
        return `Opens ${openingTime}`;
      }

      if (closingTime) {
        return `Closes ${closingTime}`;
      }

      return "Timing not available";
    }, [openingTime, closingTime]);

    // =====================================================
    // RESET ACTIVE IMAGE
    // =====================================================

    useEffect(() => {
      setActiveIndex(0);
    }, [restaurantDetails?.galleryImages]);

    useEffect(() => {
      if (activeIndex >= validImages.length) {
        setActiveIndex(0);
      }
    }, [activeIndex, validImages.length]);

    // =====================================================
    // AUTO GALLERY
    // =====================================================

    useEffect(() => {
      if (validImages.length <= 1 || isGalleryHovered) {
        return undefined;
      }

      const intervalId = window.setInterval(() => {
        setActiveIndex((previousIndex) =>
          previousIndex === validImages.length - 1 ? 0 : previousIndex + 1,
        );
      }, 3500);

      return () => window.clearInterval(intervalId);
    }, [validImages.length, isGalleryHovered]);

    // =====================================================
    // PREVIOUS IMAGE
    // =====================================================

    const handlePrevious = () => {
      if (!hasImages) {
        return;
      }

      setActiveIndex((previousIndex) =>
        previousIndex === 0 ? validImages.length - 1 : previousIndex - 1,
      );
    };

    // =====================================================
    // NEXT IMAGE
    // =====================================================

    const handleNext = () => {
      if (!hasImages) {
        return;
      }

      setActiveIndex((previousIndex) =>
        previousIndex === validImages.length - 1 ? 0 : previousIndex + 1,
      );
    };

    // =====================================================
    // BROKEN GALLERY IMAGE FALLBACK
    // =====================================================

    const handleGalleryImageError = (event, index = 0) => {
      event.currentTarget.onerror = null;

      const fallbackImage =
        FALLBACK_GALLERY_IMAGES[index % FALLBACK_GALLERY_IMAGES.length] || image1;

      event.currentTarget.src = fallbackImage;
    };

    // =====================================================
    // API DETAILS NOT AVAILABLE
    // =====================================================

    if (!restaurantDetails) {
      return (
        <section
          id="restaurant-info-section"
          className="w-full bg-blue-50 px-4 py-6 sm:px-6 lg:px-8"
        >
          <div className="mx-auto flex min-h-[180px] w-full max-w-[1200px] items-center justify-center rounded-[14px] border border-[#dfe5eb] bg-white px-5 text-center">
            <div>
              <Store size={30} className="mx-auto text-[#9aa7b8]" />

              <p className="mt-3 text-[14px] font-semibold text-[#3d4857]">
                Restaurant information is not available.
              </p>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section className="w-full bg-blue-50 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          {/* ===================================================== */}
          {/* RESTAURANT INFORMATION */}
          {/* ===================================================== */}

          <div className="overflow-hidden rounded-[14px] border border-[#dfe5eb] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
            {/* ================================================= */}
            {/* HEADER */}
            {/* ================================================= */}

            <div className="border-b border-[#e8edf2] px-5 pt-5 pb-3 sm:px-6">
              <div className="flex items-start justify-between gap-3">
                {/* ============================================= */}
                {/* LEFT - IMAGE + RESTAURANT DETAILS */}
                {/* ============================================= */}

                <div className="flex min-w-0 flex-1 items-start gap-3">
                  {/* RESTAURANT PROFILE IMAGE */}

                  <div
                    className=" h-[54px] w-[54px] shrink-0 overflow-hidden rounded-[12px] border border-[#e2e8f0] bg-[#f8fafc] sm:h-[60px] sm:w-[60px]"
                  >
                    <img
                      src={restaurantProfileImage}
                      alt={restaurantName || "Restaurant"}
                      loading="lazy"
                      className=" h-full w-full object-cover object-center"
                      onError={(event) => {
                        event.currentTarget.onerror = null;

                        event.currentTarget.src = image1;
                      }}
                    />
                  </div>

                  {/* RESTAURANT TEXT */}

                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-medium uppercase tracking-[0.5px] text-[#9aa7b8] sm:text-[11px]">
                      Restaurant Information
                    </p>

                    <h2
                      title={restaurantName || "Restaurant"}
                      className=" mt-1 line-clamp-2 break-words text-[17px] font-semibold leading-5 text-[#303947] sm:text-[20px] sm:leading-6"
                    >
                      {restaurantName || "Restaurant"}
                    </h2>

                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center gap-1 rounded-full bg-[#fff8e8] px-2 py-1">
                        <Star
                          size={13}
                          className="fill-[#f59e0b] text-[#f59e0b]"
                        />

                        <span className="text-[11px] font-semibold text-[#a16207]">
                          {rating > 0 ? rating.toFixed(1) : "No rating"}
                        </span>
                      </div>

                      <span className="text-[11px] text-[#8a96a6]">
                        {reviewCount > 0
                          ? `${reviewCount} ${reviewCount === 1 ? "review" : "reviews"}`
                          : "No reviews yet"}
                      </span>
                    </div>

                    {/* CONTACT OR ADDRESS */}

                    <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[11px] text-[#7b8797] sm:text-[12px]">
                      {phoneNumber ? (
                        <>
                          <Phone size={13} className="shrink-0" />

                          <span className="truncate">{phoneNumber}</span>
                        </>
                      ) : address ? (
                        <>
                          <MapPin size={13} className="shrink-0" />

                          <span className="truncate">{address}</span>
                        </>
                      ) : (
                        <span>Contact details not available</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* ============================================= */}
                {/* OPEN / CLOSED */}
                {/* ============================================= */}

                {/* <div
                  className={`
                    flex
                    shrink-0
                    items-center
                    gap-1.5
                    rounded-full
                    px-2.5
                    py-1.5
                    text-[9px]
                    font-semibold

                    sm:px-3
                    sm:text-[11px]

                    ${
                      isOpen
                        ? "bg-[#eaf8ef] text-[#118640]"
                        : "bg-[#fff0f0] text-[#d14343]"
                    }
                  `}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      isOpen ? "bg-[#16a34a]" : "bg-[#dc2626]"
                    }`}
                  />

                  <span className="whitespace-nowrap">
                    {isOpen ? "Open Now" : "Closed"}
                  </span>
                </div> */}
              </div>
            </div>   

            {/* ================================================= */}
            {/* INFORMATION BODY */}
            {/* ================================================= */}

            <div className="space-y-4 p-5 sm:p-6">
              {/* =============================================== */}
              {/* DELIVERY / DINING + HOURS */}
              {/* =============================================== */} 

              <div
                className="flex flex-col gap-3 rounded-[10px] border border-[#e5eaf0] bg-[#fafbfc] p-3 sm:flex-row sm:items-center sm:justify-betweensm:px-4"
              >
                {/* LEFT - SERVICES */}

                <div className="flex flex-wrap items-center gap-2">
                  {provideDelivery && (
                    <div className="flex items-center gap-1.5 rounded-full bg-[#eaf8ef] px-3 py-1.5 text-[11px] font-semibold text-[#118640]">
                      <Truck size={14} />
                      Delivery
                    </div>
                  )}

                  {provideDining && (
                    <div className="flex items-center gap-1.5 rounded-full bg-[#eef4ff] px-3 py-1.5 text-[11px] font-semibold text-[#3b68b0]">
                      <UtensilsCrossed size={14} />
                      Dining
                    </div>
                  )}

                  {!provideDelivery && !provideDining && (
                    <span className="text-[12px] text-[#9aa7b8]">
                      Service information not available
                    </span>
                  )}
                </div>

                {/* RIGHT - OPENING HOURS */}

                <div className="flex shrink-0 items-center gap-2 text-[12px] text-[#667085]">
                  <Clock3 size={15} className="shrink-0 text-[#08a66c]" />

                  <div>
                    <span className="font-medium text-[#8a96a6]">Hours:</span>{" "}
                    <span className="font-semibold text-[#3d4857]">
                      {operatingHours}
                    </span>
                  </div>
                </div>
              </div>

              {/* =============================================== */}
              {/* RESTAURANT DESCRIPTION */}
              {/* V1 API -> restaurant_description */}
              {/* =============================================== */}

              {restaurantDescription && (
                <div className="flex h-[101px] items-start gap-2.5 overflow-hidden rounded-[10px] border border-[#e5eaf0] bg-[#fafbfc] px-3 py-2 sm:h-[93px] sm:px-4 sm:py-2.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#eef4ff] text-[#3b68b0]">
                    <Building2 size={15} strokeWidth={1.9} />
                  </div>

                  <div
                    className=" h-full min-h-0 min-w-0 flex-1 overflow-y-scroll overscroll-contain touch-pan-y [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  >
                    <p className="text-[10px] font-medium uppercase leading-4 tracking-[0.4px] text-[#9aa7b8]">
                      About Restaurant
                    </p>

                    <p className="mt-0.5 break-words text-[13px] font-medium leading-[18px] text-[#3d4857] sm:text-[14px] sm:leading-[20px]">
                      {restaurantDescription}
                    </p>
                  </div>
                </div>
              )}

              {/* =============================================== */}
              {/* ADDRESS */}
              {/* =============================================== */}

              <div className="flex h-[101px] items-start gap-2.5 overflow-hidden rounded-[10px] border border-[#e5eaf0] bg-[#fafbfc] px-3 py-2 sm:h-[93px] sm:px-4 sm:py-2.5 ">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fff4e7] text-[#f59e0b]">
                  <MapPin size={15} strokeWidth={1.9} />
                </div>

                <div
                  className="
    h-full
    min-h-0
    min-w-0
    flex-1

    overflow-y-scroll
    overscroll-contain
    touch-pan-y

    [scrollbar-width:none]
    [-ms-overflow-style:none]
    [&::-webkit-scrollbar]:hidden
  "
                >
                  <p className="text-[11px] font-medium uppercase tracking-[0.4px] text-[#9aa7b8]">
                    Address
                  </p>

                  <p className="mt-0.5 whitespace-pre-line break-words text-[13px] font-medium leading-[18px] text-[#3d4857] sm:text-[14px] sm:leading-[20px]">
                    {address || "Address not available"}
                  </p>
                </div>
              </div>

              {/* =============================================== */}
              {/* LEGAL INFORMATION */}
              {/* GST + FSSAI SAME ROW */}
              {/* =============================================== */}

              <div className="grid grid-cols-2 overflow-hidden rounded-[10px] border border-[#e5eaf0] bg-[#fafbfc]">
                {/* GST */}

                <div className="flex min-w-0 items-start gap-2.5 border-r border-[#e5eaf0] p-3 sm:gap-3 sm:p-3">
                  <ReceiptText
                    size={18}
                    className="mt-0.5 shrink-0 text-[#64748b]"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="text-[9px] font-medium uppercase tracking-[0.3px] text-[#9aa7b8] sm:text-[11px]">
                      GST Number
                    </p>

                    <p
                      title={gstNumber || "Not available"}
                      className="mt-1 break-all text-[11px] font-semibold text-[#3d4857] sm:text-[13px]"
                    >
                      {gstNumber || "Not available"}
                    </p>
                  </div>
                </div>

                {/* FSSAI */}

                <div className="flex min-w-0 items-start gap-2.5 p-3 sm:gap-3 sm:p-3">
                  <BadgeCheck
                    size={18}
                    className="mt-0.5 shrink-0 text-[#6366f1]"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="text-[9px] font-medium uppercase tracking-[0.3px] text-[#9aa7b8] sm:text-[11px]">
                      FSSAI Number
                    </p>

                    <p
                      title={fssaiNumber || "Not available"}
                      className="mt-1 break-all text-[11px] font-semibold text-[#3d4857] sm:text-[13px]"
                    >
                      {fssaiNumber || "Not available"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ===================================================== */}
          {/* GALLERY */}
          {/* ===================================================== */}

          <div
            className="overflow-hidden rounded-[14px] border border-[#dfe5eb] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)]"
            onMouseEnter={() => setIsGalleryHovered(true)}
            onMouseLeave={() => setIsGalleryHovered(false)}
          >
            {/* ================================================= */}
            {/* GALLERY HEADER */}
            {/* ================================================= */}

            <div className="flex items-center justify-between border-b border-[#e8edf2] px-5 py-4 sm:px-6">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-[20px] font-semibold text-[#303947]">
                    Gallery
                  </h2>

                  {/* FALLBACK INDICATOR OPTIONAL */}
                  {isUsingFallbackGallery && (
                    <span
                      className="
                        rounded-full
                        bg-[#f1f5f9]
                        px-2
                        py-1
                        text-[8px]
                        font-semibold
                        uppercase
                        tracking-[0.03em]
                        text-[#94a3b8]

                        sm:text-[9px]
                      "
                    >
                      Preview
                    </span>
                  )}
                </div>

                <p className="mt-1 text-[12px] text-[#9aa7b8]">
                  {isUsingFallbackGallery
                    ? "Restaurant gallery preview"
                    : "Restaurant photos"}
                </p>
              </div>

              {/* ARROWS */}

              {validImages.length > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    aria-label="Previous gallery image"
                    className="
                      flex
                      h-9
                      w-9
                      items-center
                      justify-center
                      rounded-[8px]
                      border
                      border-[#dfe5eb]
                      text-[#526174]
                      transition

                      hover:border-[#08a66c]
                      hover:bg-[#effaf6]
                      hover:text-[#08a66c]

                      active:scale-95
                    "
                  >
                    <ChevronLeft size={19} />
                  </button>

                  <button
                    type="button"
                    onClick={handleNext}
                    aria-label="Next gallery image"
                    className=" flex h-9 w-9 items-center justify-center rounded-[8px] border border-[#dfe5eb] text-[#526174] transition hover:border-[#08a66c] hover:bg-[#effaf6] hover:text-[#08a66c] active:scale-95"
                  >
                    <ChevronRight size={19} />
                  </button>
                </div>
              )}
            </div>

            {/* ================================================= */}
            {/* GALLERY BODY */}
            {/* ================================================= */}

            <div className="p-3 sm:p-5">
              {/* MAIN IMAGE */}

              <div className="relative h-[280px] w-full overflow-hidden rounded-[12px] bg-[#eef1f4] sm:h-[330px]">
                <img
                  key={`${activeImage}-${activeIndex}`}
                  src={activeImage}
                  alt={`${restaurantName || "Restaurant"} gallery ${activeIndex + 1}`}
                  loading="lazy"
                  className=" h-full w-full object-cover object-center transition-opacity duration-300"
                  onError={(event) =>
                    handleGalleryImageError(event, activeIndex)
                  }
                />
              </div>

              {/* ================================================= */}
              {/* THUMBNAILS */}
              {/* ================================================= */}

              {validImages.length > 1 && (
                <div className="mt-4 flex w-full gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {validImages.map((image, index) => {
                    const isActive = activeIndex === index;

                    return (
                      <button
                        key={`${image}-${index}`}
                        type="button"
                        onClick={() => setActiveIndex(index)}
                        aria-label={`Show gallery image ${index + 1}`}
                        className={`
                            h-[68px] w-[88px] shrink-0 overflow-hidden rounded-[8px] border-2 bg-[#eef1f4] transition-all duration-200 sm:h-[76px] sm:w-[111px]

                            ${
                              isActive
                                ? "border-[#08a66c] shadow-[0_4px_12px_rgba(8,166,108,0.16)]"
                                : "border-transparent hover:border-[#aeb8c5]"
                            }
                          `}
                      >
                        <img
                          src={image}
                          alt={`${restaurantName || "Restaurant"} thumbnail ${index + 1}`}
                          loading="lazy"
                          className=" h-full w-full object-cover object-center"
                          onError={(event) =>
                            handleGalleryImageError(event, index)
                          }
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }
